import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@mantine/core';

import { Button } from '../buttons/Button';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';
import { InlineAlert } from '../inline-alert/InlineAlert';
import { Modal } from '../modal/Modal';
import { SingleSelect } from '../selects/SingleSelect';

import styles from './widget-action-modal.module.scss';

export type GenericListItem = {
  id: string;
  label: string;
  sublabel?: string;
};

export type LayoutOption = {
  value: string;
  label: string;
};

export type DetailPageCreationModalValue = {
  items: GenericListItem[];
  layout: string | null;
};

export type DetailPageCreationModalProps = {
  initialItems: GenericListItem[];
  initialLayout: string | null;
  layouts: LayoutOption[];
  listIcon: ReactNode;
  modalOpened: boolean;
  onCommit: (value: DetailPageCreationModalValue) => void;
  onCancel: () => void;
  onLayoutChange?: (layout: string | null) => void;
  size: number;
  /** When true, the Apply button is disabled. */
  applyDisabled?: boolean;
  /**
   * When set, renders an informational notice below the item list — use when
   * some selected rows have no value for the current layout's identifier column.
   */
  warningText?: string;
  withinPortal?: boolean;
  'data-testid'?: string;
};

export function DetailPageCreationModal({
  initialItems,
  initialLayout,
  layouts,
  listIcon,
  modalOpened,
  onCommit,
  onCancel,
  onLayoutChange,
  size,
  applyDisabled,
  warningText,
  withinPortal = true,
  'data-testid': testId = 'detail-page-creation-modal',
}: DetailPageCreationModalProps) {
  const [items, setItems] = useState(initialItems);
  const [layout, setLayout] = useState(initialLayout);

  const handleLayoutChange = (newLayout: string | null) => {
    setLayout(newLayout);
    onLayoutChange?.(newLayout);
  };

  const { t } = useTranslation();

  useEffect(() => {
    setItems(initialItems);
    setLayout(initialLayout);
  }, [initialItems, initialLayout, modalOpened]);

  return (
    <Modal
      actions={
        <>
          <Button
            icon={<GoogleFontIcon name="close" />}
            onClick={onCancel}
            variant="secondary"
          >
            {t('app.detailPageCreationModal.cancel')}
          </Button>
          <Button
            data-testid="detail-page-creation-modal__apply-button"
            disabled={applyDisabled}
            icon={<GoogleFontIcon name="check" />}
            onClick={() => onCommit({ items, layout })}
          >
            {t('app.detailPageCreationModal.apply')}
          </Button>
        </>
      }
      modalType="action"
      opened={modalOpened}
      onClose={onCancel}
      title={t('app.detailPageCreationModal.title')}
      withinPortal={withinPortal}
      size={size}
    >
      <div className={styles['content']} data-testid={testId}>
        <div className={styles['list']}>
          {items.map((item) => (
            <div
              key={item.id}
              className={styles['item']}
              data-testid={`detail-page-creation-modal-item-${item.id}`}
            >
              <div className={styles['item__icon']}>{listIcon}</div>
              <div className={styles['item__content']}>
                <Text classNames={{ root: styles['item__text'] }}>
                  {item.label}
                  {item.sublabel && (
                    <span className={styles['item__sublabel']}>
                      {item.sublabel}
                    </span>
                  )}
                </Text>
              </div>
            </div>
          ))}
        </div>

        {warningText && (
          <InlineAlert showIcon={false}>{warningText}</InlineAlert>
        )}

        <div className={styles['layout-selector']}>
          <SingleSelect
            label={t('app.detailPageCreationModal.layoutLabel')}
            data={layouts}
            value={layout}
            onChange={handleLayoutChange}
            withinPortal={withinPortal}
            clearable={false}
            data-testid="detail-page-creation-modal__layout-select"
          />
        </div>
      </div>
    </Modal>
  );
}
